import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface StatusCount {
  statusId: string;
  name: string;
  color: string;
  category: string;
  count: number;
}

export interface CreatedVsResolvedItem {
  period: string;
  created: number;
  resolved: number;
}

export interface PriorityCount {
  priority: string;
  count: number;
}

export interface AssigneeWorkload {
  assigneeId: string | null;
  assigneeName: string;
  statuses: Record<string, number>;
  total: number;
}

export interface BurndownData {
  data: {
    totalPoints: number;
    completedPoints: number;
    remainingPoints: number;
    issueCount: number;
  } | null;
  message?: string;
}

export interface TestExecutionSummary {
  total: number;
  executed: number;
  notRun: number;
  executionRate: number;
  statusBreakdown: Array<{ status: string; count: number }>;
}

export interface RequirementCoverage {
  requirements: { total: number; withSpec: number; coverageRate: number };
  specifications: { total: number; withTestCase: number; coverageRate: number };
}

export interface ActivityItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
  user: { id: string; name: string; email: string; avatarUrl: string | null };
  issue: { id: string; issueId: string; title: string } | null; // CHG-154
  statusLabel?: { before?: string; after?: string }; // CHG-154
  target: { key: string; title: string; deletedAt: string | null; specId?: string } | null; // CHG-157
}

// --- API functions ---

const base = (projectId: string) => `/projects/${projectId}/dashboard`;

export async function fetchIssueStatusDistribution(projectId: string) {
  const res = await api.get<ApiResponse<StatusCount[]>>(`${base(projectId)}/issue-status-distribution`);
  return res.data.data!;
}

export async function fetchCreatedVsResolved(
  projectId: string,
  params: { period: string; from?: string; to?: string },
) {
  const res = await api.get<ApiResponse<CreatedVsResolvedItem[]>>(`${base(projectId)}/created-vs-resolved`, { params });
  return res.data.data!;
}

export async function fetchPriorityDistribution(projectId: string) {
  const res = await api.get<ApiResponse<PriorityCount[]>>(`${base(projectId)}/priority-distribution`);
  return res.data.data!;
}

export async function fetchAssigneeWorkload(projectId: string) {
  const res = await api.get<ApiResponse<AssigneeWorkload[]>>(`${base(projectId)}/assignee-workload`);
  return res.data.data!;
}

export async function fetchSprintBurndown(projectId: string) {
  const res = await api.get<ApiResponse<BurndownData>>(`${base(projectId)}/sprint-burndown`);
  return res.data.data!;
}

export async function fetchTestExecutionSummary(projectId: string) {
  const res = await api.get<ApiResponse<TestExecutionSummary>>(`${base(projectId)}/test-execution-summary`);
  return res.data.data!;
}

export async function fetchRequirementCoverage(projectId: string) {
  const res = await api.get<ApiResponse<RequirementCoverage>>(`${base(projectId)}/requirement-coverage`);
  return res.data.data!;
}

export async function fetchActivities(projectId: string, limit = 20) {
  const res = await api.get<ApiResponse<ActivityItem[]>>(`${base(projectId)}/activities`, { params: { limit } });
  return res.data.data!;
}
